import { getIdentityStore } from '../identity-store'

type Listener = (hasContact: boolean) => void

const listeners = new Set<Listener>()
let lastValue: boolean | undefined

function computeValue(): boolean {
  const { traits } = getIdentityStore().getSnapshot()
  return Boolean(traits?.email) && Boolean(traits?.phone)
}

/**
 * Returns true when the SDK has captured the user's basic contact info —
 * both `email` and `phone`. Useful for gating lead-capture UI, conditional
 * tracking, or A/B splits on "qualified" visitors.
 *
 * Reads synchronously from the persisted identity store (cookie +
 * localStorage), so the answer survives reloads and cross-subdomain
 * navigation. The source is whichever call most recently fed the data:
 * `montilla.identify(userId, { email, phone })` in this session, or the
 * persisted snapshot from a previous one.
 *
 * Returns `false` when either field is missing or empty.
 *
 * @example
 * ```ts
 * if (!montilla.hasContactInfo()) {
 *   showLeadCaptureModal()
 * }
 * ```
 *
 * For reactive subscriptions, see `onContactInfoChange(cb)`.
 */
export function hasContactInfo(): boolean {
  const value = computeValue()
  // Seed `lastValue` lazily so the first internal notify after this point
  // dedups correctly (same boolean → no fire).
  if (lastValue === undefined) lastValue = value
  return value
}

/**
 * Subscribe to changes in `hasContactInfo()` state. The callback fires only
 * when the boolean actually flips:
 *   - `false → true` after `identify(...)` with email + phone
 *   - `true → false` after `reset()` clears identity
 *
 * The callback does **not** fire synchronously on subscribe with the current
 * value — the caller should read `hasContactInfo()` once for the initial
 * state.
 *
 * @returns an unsubscribe function. Call it to stop receiving notifications.
 *
 * @example
 * ```ts
 * const unsubscribe = montilla.onContactInfoChange((hasContact) => {
 *   if (hasContact) trackLeadQualified()
 * })
 * // later:
 * unsubscribe()
 * ```
 */
export function onContactInfoChange(cb: Listener): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

/**
 * @internal — wired to `identify()` and `reset()` so any state change
 * triggers a re-evaluation. Listeners only fire when the boolean actually
 * flips (deduped via `lastValue`). Errors thrown by listeners are caught
 * and logged so one bad subscriber can't break the chain.
 */
export function _notifyContactInfoChange(): void {
  const next = computeValue()
  if (next === lastValue) return
  lastValue = next
  for (const cb of listeners) {
    try {
      cb(next)
    } catch (err) {
      console.error('[montilla] onContactInfoChange listener threw:', err)
    }
  }
}

/** @internal — testing helper to reset module state between tests. */
export function _resetContactInfoState(): void {
  listeners.clear()
  lastValue = undefined
}
